import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices/DomainServicesContext";

import {
  CollectionRecord,
  CollectionResourceRef,
  CollectionResourceType,
} from "../CollectionRecord";

import { collectionQueryKeys } from "./collectionQueryKeys";

export interface CreateCollectionVariables {
  orgId: string;
  name: string;
  description?: string;
  tags?: string[];
  resourceType: CollectionResourceType;
  resources?: CollectionResourceRef[];
  customFields?: Record<string, unknown>;
}

/**
 * The single write path for creating a collection, cache maintenance included.
 *
 * Deliberately does NOT invalidate the member resource lists the way
 * {@link useUpdateCollection} does: a brand-new collection has no change
 * history to refetch, and a seeded create's members are already reflected in
 * the response written to the detail cache below.
 */
export function useCreateCollection() {
  const { collections } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<CollectionRecord, Error, CreateCollectionVariables>({
    mutationFn: ({
      orgId,
      name,
      description,
      tags,
      resourceType,
      resources,
      customFields,
    }) =>
      collections.createCollection(
        {
          name,
          description,
          tags,
          resource_type: resourceType,
          resources,
          custom_fields: customFields,
        },
        { orgId, resourceOwnerId: orgId },
      ),
    onSuccess: (record) => {
      // The response is the newest state of the record, so it is written
      // straight into the detail cache rather than invalidated and refetched.
      queryClient.setQueryData(
        collectionQueryKeys.detail(record.collection_id),
        record,
      );
      void queryClient.invalidateQueries({
        queryKey: collectionQueryKeys.lists(record.org_id),
      });
    },
  });
}
